import { useQuery } from '@apollo/client'
import { Theme, Tooltip, useMediaQuery } from '@material-ui/core'
import Divider from '@material-ui/core/Divider'
import Drawer from '@material-ui/core/Drawer'
import IconButton from '@material-ui/core/IconButton'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import ListItemText from '@material-ui/core/ListItemText'
import InventoryIcon from '@material-ui/icons//TableChart'
import WalletIcon from '@material-ui/icons/AccountBalanceWalletRounded'
import ChannelIcon from '@material-ui/icons/AccountTree'
import AccommodationIcon from '@material-ui/icons/Apartment'
import ContractIcon from '@material-ui/icons/Assignment'
import ProviderIcon from '@material-ui/icons/BusinessCenter'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import GatewaysIcon from '@material-ui/icons/CreditCard'
import DvrIcon from '@material-ui/icons/Dvr'
import TenantIcon from '@material-ui/icons/Group'
import SupportIcon from '@material-ui/icons/HeadsetMic'
import HomeIcon from '@material-ui/icons/Home'
import BookingListIcon from '@material-ui/icons/List'
import LocalOfferIcon from '@material-ui/icons/LocalOffer'
import NotificationsIcon from '@material-ui/icons/Notifications'
import PermDataSettingIcon from '@material-ui/icons/PermDataSetting'
import AccessManagementIcon from '@material-ui/icons/Person'
import PolicyIcon from '@material-ui/icons/Policy'
import SettingsIcon from '@material-ui/icons/Settings'
import NotificationsSettingsIcon from '@material-ui/icons/SettingsApplications'
import BookingIcon from '@material-ui/icons/ShoppingCart'
import TrendingUpIcon from '@material-ui/icons/TrendingUp'
import VisibilityIcon from '@material-ui/icons/Visibility'
import SiteKeyIcon from '@material-ui/icons/VpnKey'
import clsx from 'clsx'
import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'

import {
  buildFeatureSet,
  canAccessByRoutePath,
  getCredentials
} from '../../cache/auth'
import { NOTIFICATION_CAPABILITIES_QUERY } from '../../graphql/notifications'
import {
  getEntityCode,
  getEntityId,
  getEntityName,
  getRoleFromStorage
} from '../../helpers/userData'
import { getTranslate } from '../../localization'
import { LocalizedNavLink } from '../../routes/localizedRoutes'
import { RoleAccessManagement } from '../../staticRoleManagement'
import accommodationPagesRoleManagement from '../../staticRoleManagement/accommodationsRoleManagement'
import adminConfigRoleManagement from '../../staticRoleManagement/adminConfigRoleManagement'
import bookRoleManagement from '../../staticRoleManagement/bookRoleManagement'
import cancelPolicyPagesRoleManagement from '../../staticRoleManagement/cancelPolicyRoleManagement'
import channelPagesRoleManagement from '../../staticRoleManagement/channelPagesRoleManagement'
import contractPagesRoleManagement from '../../staticRoleManagement/contractRoleManagement'
import gatewayPagesRoleManagement from '../../staticRoleManagement/gatewayRoleManagement'
import inventoryPagesRoleManagement from '../../staticRoleManagement/inventoryRoleManagement'
import monitoringRoleManagement from '../../staticRoleManagement/monitoringRoleManagement'
import { MONITORING_ROUTE } from '../../staticRoleManagement/monitoringRoute'
import priceClassRoleManagement from '../../staticRoleManagement/priceClassRoleManagement'
import promotionRoleManagement from '../../staticRoleManagement/promotionRoleManagement'
import providerRoleManagement from '../../staticRoleManagement/providerRoleManagement'
import sitekeyPagesRoleManagement from '../../staticRoleManagement/sitekeyRoleManagement'
import tenantPagesRoleManagement from '../../staticRoleManagement/tenantRoleManagement'
import { ROLE } from '../../staticRoleManagement/types'
import walletPagesRoleManagement from '../../staticRoleManagement/walletRoleManagement'
import { NotificationCapabilities } from '../Notifications/notificationAccess'
import useStyles from './styles/index.styles'

interface DrawerProps {
  open: boolean
  theme: Theme
  setOpen: (val: boolean) => void
}

export const DrawerCostume: React.FC<DrawerProps> = ({
  theme,
  open,
  setOpen
}) => {
  const classes = useStyles()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const location = useLocation()

  const { data: notificationAccess } = useQuery<{
    notificationCapabilities: NotificationCapabilities
  }>(NOTIFICATION_CAPABILITIES_QUERY)
  const canReadNotificationManagement = Boolean(
    notificationAccess?.notificationCapabilities.adminRead ||
      notificationAccess?.notificationCapabilities.providerRead
  )

  const ValidChannelRoles = [ROLE.CHANNEL, ROLE.CHANNEL_ADMIN]
  const isRoleChannel = ValidChannelRoles.includes(getRoleFromStorage())

  const credentials = getCredentials()

  const featureSet = useMemo(() => buildFeatureSet(credentials), [credentials])

  return (
    <Drawer
      variant={isSmallScreen ? 'temporary' : 'permanent'}
      open={open}
      onClose={() => {
        setOpen(false)
      }}
      className={clsx(classes.drawer, {
        [classes.drawerOpen]: open,
        [classes.drawerClose]: !open
      })}
      classes={{
        paper: clsx({
          [classes.drawerOpen]: open,
          [classes.drawerClose]: !open
        })
      }}>
      <div className={classes.toolbar}>
        <IconButton onClick={() => setOpen(false)}>
          {theme.direction === 'rtl' ? (
            <ChevronRightIcon />
          ) : (
            <ChevronLeftIcon />
          )}
        </IconButton>
      </div>
      <Divider />
      <List
        className={classes.list}
        disablePadding={false}
        classes={{
          padding: classes.listPadding
        }}>
        {canAccessByRoutePath('/home', featureSet, RoleAccessManagement) && (
          <ListItem
            button
            component={LocalizedNavLink}
            to="/home"
            activeClassName={classes.activeListItem}
            onClick={() => setOpen(false)}>
            <Tooltip
              placement="right"
              title={getTranslate('Home')}
              aria-label="Home">
              <ListItemIcon>
                <HomeIcon />
              </ListItemIcon>
            </Tooltip>
            <ListItemText primary={getTranslate('Home')} />
          </ListItem>
        )}
        {canAccessByRoutePath(
          '/notifications',
          featureSet,
          RoleAccessManagement
        ) && (
          <ListItem
            button
            component={LocalizedNavLink}
            to="/notifications"
            activeClassName={classes.activeListItem}
            onClick={() => setOpen(false)}>
            <Tooltip
              placement="right"
              title={getTranslate('Notifications')}
              aria-label="Notifications">
              <ListItemIcon>
                <NotificationsIcon />
              </ListItemIcon>
            </Tooltip>
            <ListItemText primary={getTranslate('Notifications')} />
          </ListItem>
        )}
        {canAccessByRoutePath(
          '/accommodations',
          featureSet,
          accommodationPagesRoleManagement
        ) && (
          <ListItem
            button
            component={LocalizedNavLink}
            to="/accommodations"
            activeClassName={classes.activeListItem}
            onClick={() => setOpen(false)}>
            <Tooltip
              placement="right"
              title={getTranslate('Accommodations')}
              aria-label="Accommodations">
              <ListItemIcon>
                <AccommodationIcon />
              </ListItemIcon>
            </Tooltip>
            <ListItemText primary={getTranslate('Accommodations')} />
          </ListItem>
        )}
        {canAccessByRoutePath(
          '/channels',
          featureSet,
          channelPagesRoleManagement
        ) && (
          <ListItem
            button
            component={LocalizedNavLink}
            to="/channels"
            activeClassName={classes.activeListItem}
            onClick={() => setOpen(false)}>
            <Tooltip
              placement="right"
              title={getTranslate('Channels')}
              aria-label="Channels">
              <ListItemIcon>
                <ChannelIcon />
              </ListItemIcon>
            </Tooltip>
            <ListItemText primary={getTranslate('Channels')} />
          </ListItem>
        )}
        {canAccessByRoutePath(
          '/inventory',
          featureSet,
          inventoryPagesRoleManagement
        ) && (
          <ListItem
            button
            component={LocalizedNavLink}
            to="/inventory"
            // activeClassName={classes.activeListItem}
            className={
              location.pathname.includes('/inventory/') ||
              location.pathname.includes('/allocate-availability') ||
              location.pathname.includes('/allocate-history') ||
              location.pathname.includes('/allocate-to-channel')
                ? classes.activeListItem
                : ''
            }
            onClick={() => setOpen(false)}>
            <Tooltip
              placement="right"
              title={getTranslate('Inventory')}
              aria-label="Inventory">
              <ListItemIcon>
                <InventoryIcon />
              </ListItemIcon>
            </Tooltip>
            <ListItemText primary={getTranslate('Inventory')} />
          </ListItem>
        )}
        {canAccessByRoutePath(
          '/book/select-channel',
          featureSet,
          bookRoleManagement
        ) && (
          <ListItem
            button
            component={LocalizedNavLink}
            to={
              isRoleChannel
                ? `/book/${getEntityId()}/${getEntityCode()}/${getEntityName()}`
                : `/book/select-channel`
            }
            className={
              location.pathname.includes('/book/') ? classes.activeListItem : ''
            }
            onClick={() => setOpen(false)}>
            <Tooltip
              placement="right"
              title={getTranslate('Book Hotel')}
              aria-label="book hotel">
              <ListItemIcon>
                <BookingIcon />
              </ListItemIcon>
            </Tooltip>
            <ListItemText primary={getTranslate('Book Hotel')} />
          </ListItem>
        )}
        {canAccessByRoutePath(
          '/booking/list',
          featureSet,
          bookRoleManagement
        ) && (
          <ListItem
            button
            component={LocalizedNavLink}
            to="/booking/list"
            className={
              location.pathname.includes('/booking/')
                ? classes.activeListItem
                : ''
            }
            onClick={() => setOpen(false)}>
            <Tooltip
              placement="right"
              title={getTranslate('Booking List')}
              aria-label="Booking List">
              <ListItemIcon>
                <BookingListIcon />
              </ListItemIcon>
            </Tooltip>
            <ListItemText primary={getTranslate('Booking List')} />
          </ListItem>
        )}
        {canAccessByRoutePath(
          '/policies/(cancel-policies|amendment-policies)',
          featureSet,
          cancelPolicyPagesRoleManagement
        ) && (
          <ListItem
            button
            component={LocalizedNavLink}
            to="/policies/cancel-policies"
            className={
              location.pathname.includes('/policies/')
                ? classes.activeListItem
                : ''
            }
            onClick={() => setOpen(false)}>
            <Tooltip
              placement="right"
              title={getTranslate('Policies')}
              aria-label="Policies">
              <ListItemIcon>
                <PolicyIcon />
              </ListItemIcon>
            </Tooltip>
            <ListItemText primary={getTranslate('Policies')} />
          </ListItem>
        )}
        {/* <ListItem
          button
          component={LocalizedNavLink}
          to="/logs"
          activeClassName={classes.activeListItem}>
          <ListItemIcon>
            <LogsIcon />
          </ListItemIcon>
          <ListItemText primary={getTranslate('Logs')} />
        </ListItem> */}
        {canAccessByRoutePath(
          '/contracts/(list|templates)',
          featureSet,
          contractPagesRoleManagement
        ) && (
          <ListItem
            button
            component={LocalizedNavLink}
            to="/contracts/templates"
            className={
              location.pathname.includes('/contracts') ||
              location.pathname.includes('/templates')
                ? classes.activeListItem
                : ''
            }
            onClick={() => setOpen(false)}>
            <Tooltip
              placement="right"
              title={getTranslate('Contracts')}
              aria-label="Contracts">
              <ListItemIcon>
                <ContractIcon />
              </ListItemIcon>
            </Tooltip>
            <ListItemText primary={getTranslate('Contracts')} />
          </ListItem>
        )}
        {canAccessByRoutePath(
          '/promotions',
          featureSet,
          promotionRoleManagement
        ) &&
          promotionRoleManagement
            .find((item) => item.route === '/promotions')
            ?.rolesListWithReadAccess?.includes(getRoleFromStorage()) && (
            <ListItem
              button
              component={LocalizedNavLink}
              to="/promotions"
              className={
                location.pathname.includes('/promotions')
                  ? classes.activeListItem
                  : ''
              }
              onClick={() => setOpen(false)}>
              <Tooltip
                placement="right"
                title={getTranslate('Promotions')}
                aria-label={getTranslate('Promotions')}>
                <ListItemIcon>
                  <LocalOfferIcon />
                </ListItemIcon>
              </Tooltip>
              <ListItemText primary={getTranslate('Promotions')} />
            </ListItem>
          )}
      </List>
      <Divider />
      {/* <List> */}
      <List className={classes.list}>
        {canReadNotificationManagement &&
          canAccessByRoutePath(
            '/notification-management',
            featureSet,
            RoleAccessManagement
          ) && (
            <ListItem
              button
              component={LocalizedNavLink}
              to="/notification-management"
              activeClassName={classes.activeListItem}
              onClick={() => setOpen(false)}>
              <Tooltip
                placement="right"
                title={getTranslate('Notification management')}
                aria-label="Notification management">
                <ListItemIcon>
                  <NotificationsSettingsIcon />
                </ListItemIcon>
              </Tooltip>
              <ListItemText primary={getTranslate('Notification management')} />
            </ListItem>
          )}
        {canAccessByRoutePath(
          '/access-management/:page',
          featureSet,
          RoleAccessManagement
        ) && (
          <ListItem
            button
            component={LocalizedNavLink}
            to="/access-management/role-management"
            className={
              location.pathname.includes('/access-management') ||
              location.pathname.includes('user-management')
                ? classes.activeListItem
                : ''
            }
            onClick={() => setOpen(false)}>
            <Tooltip
              placement="right"
              title={getTranslate('Access Management')}
              aria-label="Access Management">
              <ListItemIcon>
                <AccessManagementIcon />
              </ListItemIcon>
            </Tooltip>
            <ListItemText primary={getTranslate('Access')} />
          </ListItem>
        )}

        {/* {RoleAccessManagement.find(
          (item) => item.route === '/access-management'
        )?.rolesListWithReadAccess?.includes(getRoleFromStorage()) && (
          <ListItem
            button
            component={LocalizedNavLink}
            to="/access-management"
            activeClassName={classes.activeListItem}
            onClick={() => setOpen(false)}>
            <Tooltip
              placement="right"
              title={getTranslate('Access Management')}
              aria-label="Access Management">
              <ListItemIcon>
                <AccessibilityIcon />
              </ListItemIcon>
            </Tooltip>
            <ListItemText primary={getTranslate('Access Management')} />
          </ListItem>
        )} */}
        {canAccessByRoutePath(
          '/provider',
          featureSet,
          providerRoleManagement
        ) && (
          <ListItem
            button
            component={LocalizedNavLink}
            to="/provider"
            activeClassName={classes.activeListItem}
            onClick={() => setOpen(false)}>
            <Tooltip
              placement="right"
              title={getTranslate('Providers')}
              aria-label="Providers">
              <ListItemIcon>
                <ProviderIcon />
              </ListItemIcon>
            </Tooltip>
            <ListItemText primary={getTranslate('Providers')} />
          </ListItem>
        )}
        {canAccessByRoutePath(
          '/price-class-managment/:page',
          featureSet,
          priceClassRoleManagement
        ) && (
          <ListItem
            button
            component={LocalizedNavLink}
            to={
              getRoleFromStorage() === ROLE.ADMIN
                ? '/price-class-managment/price-class'
                : '/price-class-managment/subscribe'
            }
            className={
              location.pathname.includes('/price-class-managment')
                ? classes.activeListItem
                : ''
            }
            onClick={() => setOpen(false)}>
            <Tooltip
              placement="right"
              title={getTranslate('Price Class Managment')}
              aria-label="Price Class Managment">
              <ListItemIcon>
                <TrendingUpIcon />
              </ListItemIcon>
            </Tooltip>
            <ListItemText primary={getTranslate('Price Class')} />
          </ListItem>
        )}
        {canAccessByRoutePath(
          '/adopay/:page',
          featureSet,
          walletPagesRoleManagement
        ) && (
          <ListItem
            button
            component={LocalizedNavLink}
            to="/adopay/wallets"
            className={
              location.pathname.includes('/adopay/')
                ? classes.activeListItem
                : ''
            }
            onClick={() => setOpen(false)}>
            <Tooltip
              placement="right"
              title={getTranslate('Adopay')}
              aria-label="Adopay">
              <ListItemIcon>
                <WalletIcon />
              </ListItemIcon>
            </Tooltip>
            <ListItemText primary={getTranslate('Adopay')} />
          </ListItem>
        )}
        {canAccessByRoutePath(
          '/tenant',
          featureSet,
          tenantPagesRoleManagement
        ) && (
          <ListItem
            button
            component={LocalizedNavLink}
            to="/tenant"
            activeClassName={classes.activeListItem}
            onClick={() => setOpen(false)}>
            <Tooltip
              placement="right"
              title={getTranslate('Tenants')}
              aria-label="Tenants">
              <ListItemIcon>
                <TenantIcon />
              </ListItemIcon>
            </Tooltip>
            <ListItemText primary={getTranslate('Tenants')} />
          </ListItem>
        )}
        {canAccessByRoutePath(
          '/gateway',
          featureSet,
          gatewayPagesRoleManagement
        ) && (
          <ListItem
            button
            component={LocalizedNavLink}
            to="/gateway"
            activeClassName={classes.activeListItem}
            onClick={() => setOpen(false)}>
            <Tooltip
              placement="right"
              title={getTranslate('Gateways')}
              aria-label="Gateways">
              <ListItemIcon>
                <GatewaysIcon />
              </ListItemIcon>
            </Tooltip>
            <ListItemText primary={getTranslate('Gateways')} />
          </ListItem>
        )}
        {canAccessByRoutePath(
          '/sitekeys',
          featureSet,
          sitekeyPagesRoleManagement
        ) && (
          <ListItem
            button
            component={LocalizedNavLink}
            to="/sitekeys"
            activeClassName={classes.activeListItem}
            onClick={() => setOpen(false)}>
            <Tooltip
              placement="right"
              title={getTranslate('Site Keys')}
              aria-label="Sitekeys">
              <ListItemIcon>
                <SiteKeyIcon />
              </ListItemIcon>
            </Tooltip>
            <ListItemText primary={getTranslate('Site Keys')} />
          </ListItem>
        )}
        {canAccessByRoutePath(
          '/admin-config/(log|security|authentication-history|tenant-export|user-sessions)',
          featureSet,
          adminConfigRoleManagement
        ) && (
          <ListItem
            button
            component={LocalizedNavLink}
            to="/admin-config/log"
            className={
              location.pathname.includes('/admin-config/')
                ? classes.activeListItem
                : ''
            }
            onClick={() => setOpen(false)}>
            <Tooltip
              placement="right"
              title={getTranslate('Admin Config')}
              aria-label="AdminConfig">
              <ListItemIcon>
                <PermDataSettingIcon />
              </ListItemIcon>
            </Tooltip>
            <ListItemText primary={getTranslate('Admin Config')} />
          </ListItem>
        )}
        {canAccessByRoutePath(
          MONITORING_ROUTE,
          featureSet,
          monitoringRoleManagement
        ) && (
          <ListItem
            button
            component={LocalizedNavLink}
            to="/monitoring/requests"
            className={
              location.pathname.includes('/monitoring')
                ? classes.activeListItem
                : ''
            }
            onClick={() => setOpen(false)}>
            <Tooltip
              placement="right"
              title={getTranslate('Monitoring')}
              aria-label="Monitoring">
              <ListItemIcon>
                <VisibilityIcon />
              </ListItemIcon>
            </Tooltip>
            <ListItemText primary={getTranslate('Monitoring')} />
          </ListItem>
        )}
        {canAccessByRoutePath(
          '/audit-logs',
          featureSet,
          RoleAccessManagement
        ) && (
          <ListItem
            button
            component={LocalizedNavLink}
            to="/audit-logs"
            activeClassName={classes.activeListItem}
            onClick={() => setOpen(false)}>
            <Tooltip
              placement="right"
              title={getTranslate('Audit Logs')}
              aria-label="Audit Logs">
              <ListItemIcon>
                <DvrIcon />
              </ListItemIcon>
            </Tooltip>
            <ListItemText primary={getTranslate('Audit Logs')} />
          </ListItem>
        )}

        {/* {reportingRoleManagement
          .find((item) => item.route === '/reports/:page')
          ?.rolesListWithReadAccess?.includes(getRoleFromStorage()) && (
          <ListItem
            button
            component={LocalizedNavLink}
            to="/reports/payment"
            className={
              location.pathname.includes('/reports/')
                ? classes.activeListItem
                : ''
            }
            disabled={getRoleFromStorage() !== 'ADMIN'}
            onClick={() => setOpen(false)}>
            <Tooltip
              placement="right"
              title={getTranslate('Reports')}
              aria-label="Reports">
              <ListItemIcon>
                <DescriptionIcon />
              </ListItemIcon>
            </Tooltip>
            <ListItemText primary={getTranslate('Reports')} />
          </ListItem>
        )} */}
        {/* ONE entry for the whole section. The agent queue and SLA policies
            used to sit here as siblings and were shown to everyone — the
            canAccessByRoutePath gate returns true whenever requiredFeatures is
            empty, so listing ROLE.ADMIN on those routes hid nothing. They are
            now admin-only TABS inside SupportTabs, which is what actually
            decides. */}
        {canAccessByRoutePath(
          '/support/:page',
          featureSet,
          RoleAccessManagement
        ) && (
          <ListItem
            button
            component={LocalizedNavLink}
            to="/support"
            className={
              location.pathname.includes('/support/')
                ? classes.activeListItem
                : ''
            }
            activeClassName={classes.activeListItem}
            onClick={() => setOpen(false)}>
            <Tooltip
              placement="right"
              title={getTranslate('Support')}
              aria-label="Support">
              <ListItemIcon>
                <SupportIcon />
              </ListItemIcon>
            </Tooltip>
            <ListItemText primary={getTranslate('Support')} />
          </ListItem>
        )}
        {canAccessByRoutePath(
          '/settings',
          featureSet,
          RoleAccessManagement
        ) && (
          <ListItem
            button
            component={LocalizedNavLink}
            to="/settings/basic-info"
            activeClassName={classes.activeListItem}
            onClick={() => setOpen(false)}>
            <Tooltip
              placement="right"
              title={getTranslate('Settings')}
              aria-label="Settings">
              <ListItemIcon>
                <SettingsIcon />
              </ListItemIcon>
            </Tooltip>
            <ListItemText primary={getTranslate('Settings')} />
          </ListItem>
        )}
      </List>
    </Drawer>
  )
}
